import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DatePickerField } from './DatePickerField';
import {
  trackerApi,
  type SurebetHistoryApiItem,
  type TrackerCostApiInput,
  type TrackerCostApiUpdate,
  type TrackedSurebetApiInput,
  type TrackedSurebetApiUpdate,
} from './api';
import { PublicLayout } from './PublicLayout';
import { createExcelWorkbook, downloadBlob } from './excelExport';
import { TRACKER_MODE, isTrackerApiMode, isTrackerHistoryApiMode, isTrackerLocalMode } from './services/trackerMode';

type TrackerTab = 'toevoegen' | 'resultaten' | 'mijn-surebets' | 'kosten';
type HistoryFilter = 'today' | 'yesterday' | 'week' | 'date';
type BetType = 'Pre-match' | 'Live';
type HistoryTypeFilter = 'all' | BetType;
type BetStatus = 'Open' | 'Afgerond' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
type BetResult = 'Nog niet bekend' | 'A gewonnen' | 'B gewonnen' | 'C gewonnen' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
type PaymentType = 'Abonnement' | 'Kosten' | 'Correctie';
type ManualSurebetForm = {
  eventName: string;
  sport: string;
  competition: string;
  market: string;
  selection: string;
  line: string;
  type: BetType;
  bookmakerA: string;
  directionA: string;
  bookmakerB: string;
  directionB: string;
  bookmakerC: string;
  directionC: string;
};

type SurebetSortOption =
  | 'nieuwste'
  | 'oudste'
  | 'hoogste-resultaat'
  | 'laagste-resultaat'
  | 'hoogste-roi'
  | 'laagste-roi'
  | 'hoogste-inzet'
  | 'laagste-inzet';

type PeriodFilter = 'alles' | 'vandaag' | 'gisteren' | 'week7' | 'deze-maand' | 'vorige-maand';

type SurebetHistoryItem = {
  id: string;
  date: string;
  eventName: string;
  sport: string;
  competition?: string | null;
  market: string;
  selection?: string | null;
  line?: number | null;
  type: BetType;
  bookmakerA: string;
  directionA: string;
  oddA: number;
  suggestedStakeA: number;
  bookmakerB: string;
  directionB: string;
  oddB: number;
  suggestedStakeB: number;
  bookmakerC?: string | null;
  directionC?: string | null;
  oddC?: number | null;
  suggestedStakeC?: number | null;
  totalStake?: number;
  minimumProfit?: number;
  roiPercent?: number;
};

type TrackedSurebet = {
  id: string;
  date: string;
  surebetId: string;
  eventName: string;
  sport?: string;
  competition?: string | null;
  market: string;
  selection?: string | null;
  line?: number | null;
  type: BetType;
  bookmakerA?: string;
  directionA?: string;
  oddA: number;
  originalOddA?: number;
  oddB: number;
  originalOddB?: number;
  stakeA: number;
  stakeB: number;
  bookmakerB?: string;
  directionB?: string;
  bookmakerC?: string | null;
  directionC?: string | null;
  oddC?: number | null;
  originalOddC?: number | null;
  stakeC?: number | null;
  totalStake: number;
  payoutA: number;
  payoutB: number;
  payoutC?: number | null;
  profitA: number;
  profitB: number;
  profitC?: number | null;
  minimumProfit: number;
  roi: number;
  roiPercent?: number;
  finalResult?: number;
  status: BetStatus;
  result: BetResult;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  oddsWereChanged?: boolean;
  stakeWasChanged?: boolean;
};

type Payment = {
  id: string;
  date: string;
  type: PaymentType;
  description: string;
  amount: number;
  source?: 'manual' | 'stripe';
  automatic?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

const TRACKED_KEY = 'perfectodds_tracked_surebets';
const PAYMENTS_KEY = 'perfectodds_payments';
const MANUAL_MARKET_OPTIONS = [
  { value: '1X2', label: '1X2 - Thuis / Gelijkspel / Uit' },
  { value: 'Total Goals', label: 'Total goals - Over/Under' },
  { value: 'Both Teams To Score', label: 'Both Teams To Score - Ja/Nee' },
  { value: 'Asian Handicap', label: 'Asian handicap' },
  { value: 'Total Shots', label: 'Total shots - Over/Under' },
  { value: 'Total Shots on Target', label: 'Total shots on target - Over/Under' },
  { value: 'Player Shots', label: 'Player shots - Over/Under' },
  { value: 'Player Shots on Target', label: 'Player shots on target - Over/Under' },
  { value: 'Team Shots', label: 'Team shots - Over/Under' },
  { value: 'Team Shots on Target', label: 'Team shots on target - Over/Under' },
];

type TrackerLoadError = {
  message: string;
  accessDenied: boolean;
};

type TrackerToast = {
  title: string;
  message: string;
  tone: 'success' | 'error';
  action: 'mijn-surebets' | null;
};

function useLocalStorageState(key: string, initialValue: any, enabled = true) {
  const [value, setValue] = useState<any>(() => {
    if (!enabled) return initialValue;
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    if (!enabled) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore localStorage errors
    }
  }, [enabled, key, value]);

  return [value, setValue] as const;
}

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const normalizeCostAmount = (type: PaymentType, amount: number): number => (
  type === 'Correctie' ? amount : -Math.abs(amount)
);

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(round2(value));

const formatPercent = (value: number): string => `${round2(value).toFixed(2).replace('.', ',')}%`;

const formatMarketLine = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value);
};

const formatTrackerMarket = (item: { market: string; selection?: string | null; line?: number | null }): string => {
  const market = item.market.trim();
  const selection = item.selection?.trim();
  const line = formatMarketLine(item.line);
  const marketKey = market.toLowerCase();

  if (marketKey.includes('shot') && line) {
    const metric = marketKey.includes('target') || marketKey.includes('op doel')
      ? 'Shots on Target Over/Under'
      : 'Shots Over/Under';
    if (!selection) return `${market} ${line} Over/Under`;
    return `${selection} ${line} ${metric}`;
  }

  return market;
};

const toInputDate = (value: Date): string => {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toAmsterdamInputDate = (value: Date): string => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const today = toInputDate(new Date());
const yesterday = toInputDate(new Date(Date.now() - 24 * 60 * 60 * 1000));

const formatDateLabel = (date: string): string => {
  if (date === today) return 'Vandaag';
  if (date === yesterday) return 'Gisteren';
  const parsed = new Date(`${date}T00:00:00`);
  return parsed.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
};

const toDateOnly = (value?: string | null): string => {
  if (!value) return today;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return toAmsterdamInputDate(parsed);
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeBetType = (value: unknown): BetType => (value === 'Live' ? 'Live' : 'Pre-match');

const normalizeBetStatus = (value: unknown): BetStatus => {
  if (value === 'Open' || value === 'Afgerond' || value === 'Void' || value === 'Deels void' || value === 'Niet geplaatst' || value === 'Fout geplaatst') {
    return value;
  }
  return 'Open';
};

const normalizeBetResult = (value: unknown): BetResult => {
  if (
    value === 'Nog niet bekend'
    || value === 'A gewonnen'
    || value === 'B gewonnen'
    || value === 'C gewonnen'
    || value === 'Void'
    || value === 'Deels void'
    || value === 'Niet geplaatst'
    || value === 'Fout geplaatst'
  ) {
    return value;
  }
  return 'Nog niet bekend';
};

const getStatusForResult = (value: BetResult): BetStatus => {
  if (value === 'A gewonnen' || value === 'B gewonnen') return 'Afgerond';
  if (value === 'Void' || value === 'Deels void') return 'Void';
  return 'Open';
};

const toEditableBetResult = (value: BetResult): BetResult => {
  if (value === 'A gewonnen' || value === 'B gewonnen' || value === 'Void' || value === 'Nog niet bekend') return value;
  if (value === 'Deels void') return 'Void';
  return 'Nog niet bekend';
};

const normalizePaymentType = (value: unknown): PaymentType => {
  if (value === 'Abonnement' || value === 'Kosten' || value === 'Correctie') {
    return value;
  }
  return 'Kosten';
};

const toPlacedAtValue = (date: string): string => `${date}T00:00:00`;

const getTrackerApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const status = typeof error === 'object' && error && 'response' in error
    ? (error as { response?: { status?: number } }).response?.status
    : undefined;

  if (status === 401) return 'Je sessie is verlopen. Log opnieuw in.';
  if (status === 403) return 'Je hebt geen toegang tot de tracker.';
  if (status && status >= 500) return 'De tracker-API is tijdelijk niet bereikbaar.';
  if (typeof error === 'object' && error && 'request' in error) {
    return 'De tracker-API is tijdelijk niet bereikbaar.';
  }
  return fallbackMessage;
};

const mapTrackerFormToApiCreatePayload = (
  form: {
    selectedDate: string;
    status: BetStatus;
    result: BetResult;
    oddA: string;
    oddB: string;
    stakeA: string;
    stakeB: string;
    oddC: string;
    stakeC: string;
  },
  selectedSurebet: SurebetHistoryItem,
  options?: { manualEntry?: boolean },
): TrackedSurebetApiInput => ({
  surebetHistoryId: options?.manualEntry ? null : selectedSurebet.id,
  placedAt: toPlacedAtValue(form.selectedDate),
  eventName: selectedSurebet.eventName,
  sport: selectedSurebet.sport,
  competition: selectedSurebet.competition ?? null,
  market: selectedSurebet.market,
  selection: selectedSurebet.selection ?? null,
  line: selectedSurebet.line ?? null,
  type: selectedSurebet.type,
  status: form.status,
  result: form.result,
  notes: null,
  bookmakerA: selectedSurebet.bookmakerA,
  directionA: selectedSurebet.directionA,
  originalOddA: selectedSurebet.oddA,
  placedOddA: parseNumber(form.oddA) ?? 0,
  stakeA: parseNumber(form.stakeA) ?? 0,
  bookmakerB: selectedSurebet.bookmakerB,
  directionB: selectedSurebet.directionB,
  originalOddB: selectedSurebet.oddB,
  placedOddB: parseNumber(form.oddB) ?? 0,
  stakeB: parseNumber(form.stakeB) ?? 0,
  bookmakerC: selectedSurebet.bookmakerC ?? null,
  directionC: selectedSurebet.directionC ?? null,
  originalOddC: selectedSurebet.oddC ?? null,
  placedOddC: parseNumber(form.oddC) ?? selectedSurebet.oddC ?? null,
  stakeC: parseNumber(form.stakeC) ?? selectedSurebet.suggestedStakeC ?? null,
});

const mapTrackerEditToApiUpdatePayload = (form: {
  selectedDate: string;
  status: BetStatus;
  result: BetResult;
  oddA: string;
  oddB: string;
  stakeA: string;
  stakeB: string;
  oddC: string;
  stakeC: string;
}): TrackedSurebetApiUpdate => ({
  placedAt: toPlacedAtValue(form.selectedDate),
  placedOddA: parseNumber(form.oddA) ?? undefined,
  placedOddB: parseNumber(form.oddB) ?? undefined,
  placedOddC: parseNumber(form.oddC) ?? undefined,
  stakeA: parseNumber(form.stakeA) ?? undefined,
  stakeB: parseNumber(form.stakeB) ?? undefined,
  stakeC: parseNumber(form.stakeC) ?? undefined,
  status: form.status,
  result: form.result,
  notes: null,
});

const mapCostFormToApiCreatePayload = (form: {
  paymentDate: string;
  paymentType: PaymentType;
  paymentDescription: string;
  paymentAmount: string;
}): TrackerCostApiInput => ({
  date: form.paymentDate,
  type: form.paymentType,
  description: form.paymentDescription.trim(),
  amount: normalizeCostAmount(form.paymentType, parseNumber(form.paymentAmount) ?? 0),
});

const mapCostEditToApiUpdatePayload = (form: {
  paymentDate: string;
  paymentType: PaymentType;
  paymentDescription: string;
  paymentAmount: string;
}): TrackerCostApiUpdate => ({
  date: form.paymentDate,
  type: form.paymentType,
  description: form.paymentDescription.trim(),
  amount: parseNumber(form.paymentAmount) == null
    ? undefined
    : normalizeCostAmount(form.paymentType, parseNumber(form.paymentAmount) as number),
});

const mapApiSurebetToTrackerSurebet = (apiBet: Record<string, unknown>): TrackedSurebet => ({
  id: String(apiBet.id ?? ''),
  date: toDateOnly((apiBet.placedAt as string | undefined) ?? (apiBet.createdAt as string | undefined)),
  surebetId: String(apiBet.surebetHistoryId ?? apiBet.id ?? ''),
  eventName: String(apiBet.eventName ?? ''),
  sport: typeof apiBet.sport === 'string' ? apiBet.sport : undefined,
  competition: typeof apiBet.competition === 'string' ? apiBet.competition : null,
  market: String(apiBet.market ?? ''),
  selection: typeof apiBet.selection === 'string' ? apiBet.selection : null,
  line: apiBet.line == null ? null : toNumber(apiBet.line),
  type: normalizeBetType(apiBet.type),
  bookmakerA: typeof apiBet.bookmakerA === 'string' ? apiBet.bookmakerA : undefined,
  directionA: typeof apiBet.directionA === 'string' ? apiBet.directionA : undefined,
  oddA: toNumber(apiBet.placedOddA),
  originalOddA: toNumber(apiBet.originalOddA),
  oddB: toNumber(apiBet.placedOddB),
  originalOddB: toNumber(apiBet.originalOddB),
  stakeA: toNumber(apiBet.stakeA),
  stakeB: toNumber(apiBet.stakeB),
  bookmakerB: typeof apiBet.bookmakerB === 'string' ? apiBet.bookmakerB : undefined,
  directionB: typeof apiBet.directionB === 'string' ? apiBet.directionB : undefined,
  bookmakerC: typeof apiBet.bookmakerC === 'string' ? apiBet.bookmakerC : null,
  directionC: typeof apiBet.directionC === 'string' ? apiBet.directionC : null,
  oddC: apiBet.placedOddC == null ? null : toNumber(apiBet.placedOddC),
  originalOddC: apiBet.originalOddC == null ? null : toNumber(apiBet.originalOddC),
  stakeC: apiBet.stakeC == null ? null : toNumber(apiBet.stakeC),
  totalStake: toNumber(apiBet.totalStake),
  payoutA: toNumber(apiBet.payoutA),
  payoutB: toNumber(apiBet.payoutB),
  payoutC: apiBet.payoutC == null ? null : toNumber(apiBet.payoutC),
  profitA: toNumber(apiBet.profitA),
  profitB: toNumber(apiBet.profitB),
  profitC: apiBet.profitC == null ? null : toNumber(apiBet.profitC),
  minimumProfit: toNumber(apiBet.minimumProfit),
  roi: toNumber(apiBet.roiPercent),
  roiPercent: toNumber(apiBet.roiPercent),
  finalResult: toNumber(apiBet.finalResult),
  status: normalizeBetStatus(apiBet.status),
  result: normalizeBetResult(apiBet.result),
  notes: typeof apiBet.notes === 'string' ? apiBet.notes : null,
  createdAt: typeof apiBet.createdAt === 'string' ? apiBet.createdAt : undefined,
  updatedAt: typeof apiBet.updatedAt === 'string' ? apiBet.updatedAt : undefined,
  oddsWereChanged: Boolean(apiBet.oddsWereChanged),
  stakeWasChanged: Boolean(apiBet.stakeWasChanged),
});

const mapApiCostToTrackerCost = (apiCost: Record<string, unknown>): Payment => ({
  id: String(apiCost.id ?? ''),
  date: toDateOnly(apiCost.date as string | undefined),
  type: normalizePaymentType(apiCost.type),
  description: String(apiCost.description ?? ''),
  amount: toNumber(apiCost.amount),
  source: apiCost.source === 'stripe' ? 'stripe' : 'manual',
  automatic: Boolean(apiCost.automatic),
  createdAt: typeof apiCost.createdAt === 'string' ? apiCost.createdAt : undefined,
  updatedAt: typeof apiCost.updatedAt === 'string' ? apiCost.updatedAt : undefined,
});

const mapApiHistoryToSurebetHistory = (apiHistory: SurebetHistoryApiItem): SurebetHistoryItem => ({
  id: apiHistory.id,
  date: toDateOnly(apiHistory.date),
  eventName: apiHistory.eventName,
  sport: apiHistory.sport,
  competition: apiHistory.competition ?? null,
  market: apiHistory.market,
  selection: apiHistory.selection ?? null,
  line: apiHistory.line ?? null,
  type: normalizeBetType(apiHistory.type),
  bookmakerA: apiHistory.bookmakerA,
  directionA: apiHistory.directionA,
  oddA: toNumber(apiHistory.oddA),
  suggestedStakeA: toNumber(apiHistory.suggestedStakeA),
  bookmakerB: apiHistory.bookmakerB,
  directionB: apiHistory.directionB,
  oddB: toNumber(apiHistory.oddB),
  suggestedStakeB: toNumber(apiHistory.suggestedStakeB),
  bookmakerC: apiHistory.bookmakerC ?? null,
  directionC: apiHistory.directionC ?? null,
  oddC: apiHistory.oddC == null ? null : toNumber(apiHistory.oddC),
  suggestedStakeC: apiHistory.suggestedStakeC == null ? null : toNumber(apiHistory.suggestedStakeC),
  totalStake: toNumber(apiHistory.totalStake),
  minimumProfit: toNumber(apiHistory.minimumProfit),
  roiPercent: toNumber(apiHistory.roiPercent),
});

const EMPTY_HISTORY: SurebetHistoryItem[] = [];

const calculateOddsRoi = (oddA: number, oddB: number, oddC?: number | null): number => {
  if (oddA <= 1 || oddB <= 1 || (oddC != null && oddC <= 1)) return 0;
  const impliedProbability = (1 / oddA) + (1 / oddB) + (oddC != null ? 1 / oddC : 0);
  if (impliedProbability <= 0) return 0;
  return ((1 / impliedProbability) - 1) * 100;
};

const calculateBet = (
  oddA: number,
  oddB: number,
  stakeA: number,
  stakeB: number,
  oddC?: number | null,
  stakeC?: number | null,
) => {
  const safeStakeC = oddC != null && stakeC != null ? stakeC : 0;
  const totalStake = stakeA + stakeB + safeStakeC;
  const payoutA = oddA * stakeA;
  const payoutB = oddB * stakeB;
  const payoutC = oddC != null && safeStakeC > 0 ? oddC * safeStakeC : null;
  const profitA = payoutA - totalStake;
  const profitB = payoutB - totalStake;
  const profitC = payoutC != null ? payoutC - totalStake : null;
  const minimumProfit = Math.min(profitA, profitB, profitC ?? Number.POSITIVE_INFINITY);
  const roi = totalStake > 0 ? (minimumProfit / totalStake) * 100 : 0;
  const oddsRoi = calculateOddsRoi(oddA, oddB, oddC);
  return {
    totalStake: round2(totalStake),
    payoutA: round2(payoutA),
    payoutB: round2(payoutB),
    payoutC: payoutC == null ? null : round2(payoutC),
    profitA: round2(profitA),
    profitB: round2(profitB),
    profitC: profitC == null ? null : round2(profitC),
    minimumProfit: round2(minimumProfit),
    roi: round2(roi),
    oddsRoi: round2(oddsRoi),
  };
};

const getFinalResult = (bet: TrackedSurebet): number => {
  if (typeof bet.finalResult === 'number') {
    if (bet.status === 'Open' || bet.result === 'Nog niet bekend') return 0;
    return round2(bet.finalResult);
  }
  if (bet.status === 'Open' || bet.result === 'Nog niet bekend') return 0;
  if (bet.result === 'A gewonnen') return round2(bet.profitA);
  if (bet.result === 'B gewonnen') return round2(bet.profitB);
  if (bet.result === 'C gewonnen') return round2(bet.profitC ?? 0);
  if (bet.result === 'Void' || bet.result === 'Niet geplaatst') return 0;
  return 0;
};

const getFinalRoi = (bet: TrackedSurebet): number => {
  if (bet.status === 'Open' || bet.result === 'Nog niet bekend') return 0;
  const totalStake = Number(bet.totalStake ?? 0);
  if (totalStake <= 0) return 0;
  return (getFinalResult(bet) / totalStake) * 100;
};

const calculateStats = (bets: TrackedSurebet[], payments: Payment[]) => {
  const totalStake = bets.reduce((sum, b) => sum + b.totalStake, 0);
  const totalSurebetWinst = bets.reduce((sum, b) => sum + getFinalResult(b), 0);
  const openCount = bets.filter((b) => b.status === 'Open').length;
  const completedCount = bets.filter((b) => b.status === 'Afgerond').length;

  const roiList = bets
    .filter((b) => b.status === 'Afgerond')
    .map((b) => {
      const result = getFinalResult(b);
      return b.totalStake > 0 ? (result / b.totalStake) * 100 : 0;
    });
  const avgRoi = roiList.length > 0 ? roiList.reduce((s, v) => s + v, 0) / roiList.length : 0;

  const paymentTotal = payments.reduce((sum, p) => sum + p.amount, 0);
  const netResult = totalSurebetWinst + paymentTotal;

  return {
    totalSurebetWinst: round2(totalSurebetWinst),
    totalStake: round2(totalStake),
    avgRoi: round2(avgRoi),
    openCount,
    completedCount,
    netResult: round2(netResult),
  };
};

export const DashboardPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page tracker-page">
      <section className="subscription-hero tracker-hero">
        <h1>Dashboard</h1>
        <p>Kies een onderdeel binnen je trackeromgeving.</p>
      </section>
      <section className="subscription-section">
        <div className="tracker-dashboard-grid">
          <article className="card tracker-dashboard-card">
            <h3>Mijn tracker</h3>
            <p>Voeg bets toe, bekijk je sheet en volg je resultaten.</p>
            <Link to="/tracker" className="card-btn card-link">Open tracker</Link>
          </article>
        </div>
      </section>
    </section>
  </PublicLayout>
);

const TrackerWorkspacePage: React.FC = () => {
  const HISTORY_PAGE_SIZE = 8;
  const TRACKED_SUREBETS_PAGE_SIZE = 8;
  const trackerApiMode = isTrackerApiMode();
  const trackerHistoryApiMode = isTrackerHistoryApiMode();
  const trackerLocalMode = isTrackerLocalMode();
  const trackerNeedsBackendLoad = trackerApiMode || trackerHistoryApiMode;
  const [activeTab, setActiveTab] = useState<TrackerTab>('toevoegen');
  const saveToastTimerRef = useRef<number | null>(null);
  const mySurebetsSectionRef = useRef<HTMLElement | null>(null);
  const [trackedSurebets, setTrackedSurebets] = useLocalStorageState(TRACKED_KEY, [], trackerLocalMode);
  const [payments, setPayments] = useLocalStorageState(PAYMENTS_KEY, [], trackerLocalMode);
  const [apiHistory, setApiHistory] = useState<SurebetHistoryItem[]>([]);
  const [apiTrackedSurebets, setApiTrackedSurebets] = useState<TrackedSurebet[]>([]);
  const [apiPayments, setApiPayments] = useState<Payment[]>([]);
  const [trackerLoadState, setTrackerLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>(trackerNeedsBackendLoad ? 'loading' : 'loaded');
  const [trackerLoadError, setTrackerLoadError] = useState<TrackerLoadError | null>(null);
  const [toast, setToast] = useState<TrackerToast | null>(null);
  const [isSavingSurebet, setIsSavingSurebet] = useState(false);
  const [isUpdatingSurebet, setIsUpdatingSurebet] = useState(false);
  const [isDeletingSurebetId, setIsDeletingSurebetId] = useState<string | null>(null);
  const [isSavingCost, setIsSavingCost] = useState(false);
  const [isUpdatingCost, setIsUpdatingCost] = useState(false);
  const [isDeletingCostId, setIsDeletingCostId] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState(today);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('today');
  const [historyTypeFilter, setHistoryTypeFilter] = useState<HistoryTypeFilter>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedSurebetId, setSelectedSurebetId] = useState('');
  const [isManualSurebetMode, setIsManualSurebetMode] = useState(false);
  const [manualSurebetForm, setManualSurebetForm] = useState<ManualSurebetForm>({
    eventName: '',
    sport: 'soccer',
    competition: '',
    market: MANUAL_MARKET_OPTIONS[0].value,
    selection: '',
    line: '',
    type: 'Pre-match',
    bookmakerA: '',
    directionA: 'Over',
    bookmakerB: '',
    directionB: 'Under',
    bookmakerC: '',
    directionC: '',
  });
  const [editingTrackedId, setEditingTrackedId] = useState<string | null>(null);
  const historyItems = trackerHistoryApiMode ? apiHistory : EMPTY_HISTORY;
  const selectedSurebet: SurebetHistoryItem | undefined = useMemo(
    () => historyItems.find((s) => s.id === selectedSurebetId),
    [historyItems, selectedSurebetId],
  );

  const [oddA, setOddA] = useState('');
  const [oddB, setOddB] = useState('');
  const [oddC, setOddC] = useState('');
  const [stakeA, setStakeA] = useState('');
  const [stakeB, setStakeB] = useState('');
  const [stakeC, setStakeC] = useState('');
  const [status, setStatus] = useState<BetStatus>('Open');
  const [result, setResult] = useState<BetResult>('Nog niet bekend');
  const manualSurebet: SurebetHistoryItem | undefined = useMemo(() => {
    if (!isManualSurebetMode) return undefined;
    const eventName = manualSurebetForm.eventName.trim();
    const market = manualSurebetForm.market.trim();
    const bookmakerA = manualSurebetForm.bookmakerA.trim();
    const bookmakerB = manualSurebetForm.bookmakerB.trim();
    const directionA = manualSurebetForm.directionA.trim();
    const directionB = manualSurebetForm.directionB.trim();
    if (!eventName || !market || !bookmakerA || !bookmakerB || !directionA || !directionB) return undefined;

    return {
      id: 'manual',
      date: selectedDate,
      eventName,
      sport: manualSurebetForm.sport.trim() || 'soccer',
      competition: manualSurebetForm.competition.trim() || null,
      market,
      selection: manualSurebetForm.selection.trim() || null,
      line: parseNumber(manualSurebetForm.line),
      type: manualSurebetForm.type,
      bookmakerA,
      directionA,
      oddA: parseNumber(oddA) ?? 0,
      suggestedStakeA: parseNumber(stakeA) ?? 0,
      bookmakerB,
      directionB,
      oddB: parseNumber(oddB) ?? 0,
      suggestedStakeB: parseNumber(stakeB) ?? 0,
      bookmakerC: manualSurebetForm.bookmakerC.trim() || null,
      directionC: manualSurebetForm.directionC.trim() || null,
      oddC: parseNumber(oddC),
      suggestedStakeC: parseNumber(stakeC),
    };
  }, [isManualSurebetMode, manualSurebetForm, oddA, oddB, oddC, selectedDate, stakeA, stakeB, stakeC]);
  const activeSurebet = isManualSurebetMode ? manualSurebet : selectedSurebet;

  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentType, setPaymentType] = useState<PaymentType>('Kosten');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

  // Filter state for "Mijn surebets"
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [filterPeriod, setFilterPeriod] = useState<PeriodFilter>('alles');
  const [filterSort, setFilterSort] = useState<SurebetSortOption>('nieuwste');
  const [trackedPage, setTrackedPage] = useState(1);

  const loadTrackerData = useCallback(async () => {
    if (!trackerNeedsBackendLoad) return;

    setTrackerLoadState('loading');
    setTrackerLoadError(null);

    try {
      const [historyResponse, surebetsResponse, costsResponse] = await Promise.all([
        trackerHistoryApiMode ? trackerApi.getTrackerHistory({ includeCurrentDay: true }) : Promise.resolve({ data: [] as SurebetHistoryApiItem[] }),
        trackerApiMode ? trackerApi.getTrackedSurebets() : Promise.resolve({ data: [] as Record<string, unknown>[] }),
        trackerApiMode ? trackerApi.getTrackerCosts() : Promise.resolve({ data: [] as Record<string, unknown>[] }),
      ]);

      const nextHistory = Array.isArray(historyResponse.data)
        ? historyResponse.data.map((item: SurebetHistoryApiItem) => mapApiHistoryToSurebetHistory(item))
        : [];
      const nextSurebets = Array.isArray(surebetsResponse.data)
        ? surebetsResponse.data.map((item: Record<string, unknown>) => mapApiSurebetToTrackerSurebet(item))
        : [];
      const nextCosts = Array.isArray(costsResponse.data)
        ? costsResponse.data.map((item: Record<string, unknown>) => mapApiCostToTrackerCost(item))
        : [];

      setApiHistory(nextHistory);
      setApiTrackedSurebets(nextSurebets);
      setApiPayments(nextCosts);
      setTrackerLoadState('loaded');
    } catch (error: unknown) {
      const status = typeof error === 'object' && error && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
      const accessDenied = status === 401 || status === 403;
      setTrackerLoadError({
        accessDenied,
        message: getTrackerApiErrorMessage(error, 'Controleer of je bent ingelogd en of de backend bereikbaar is.'),
      });
      setTrackerLoadState('error');
      setApiHistory([]);
      setApiTrackedSurebets([]);
      setApiPayments([]);
    }
  }, [trackerApiMode, trackerHistoryApiMode, trackerNeedsBackendLoad]);

  useEffect(() => {
    return () => {
      if (saveToastTimerRef.current !== null) {
        window.clearTimeout(saveToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!trackerNeedsBackendLoad) {
      setTrackerLoadState('loaded');
      setTrackerLoadError(null);
      return;
    }
    void loadTrackerData();
  }, [loadTrackerData, trackerNeedsBackendLoad]);

  useEffect(() => {
    if (editingTrackedId) return;
    if (isManualSurebetMode) return;
    if (historyItems.length === 0) {
      setSelectedSurebetId('');
      return;
    }
    if (selectedSurebetId && !historyItems.some((item) => item.id === selectedSurebetId)) {
      setSelectedSurebetId('');
    }
  }, [editingTrackedId, historyItems, isManualSurebetMode, selectedSurebetId]);

  useEffect(() => {
    if (editingTrackedId) return;
    if (isManualSurebetMode) return;
    if (!selectedSurebet) {
      setOddA('');
      setOddB('');
      setOddC('');
      setStakeA('');
      setStakeB('');
      setStakeC('');
      return;
    }
    setOddA(String(selectedSurebet.oddA));
    setOddB(String(selectedSurebet.oddB));
    setOddC(selectedSurebet.oddC == null ? '' : String(selectedSurebet.oddC));
    setStakeA('');
    setStakeB('');
    setStakeC('');
  }, [selectedSurebetId, selectedSurebet, editingTrackedId, isManualSurebetMode]);

  const preview = useMemo(() => {
    const a = parseNumber(oddA);
    const b = parseNumber(oddB);
    const c = parseNumber(oddC);
    const sa = parseNumber(stakeA);
    const sb = parseNumber(stakeB);
    const sc = parseNumber(stakeC);
    const needsThirdLeg = Boolean(activeSurebet?.oddC) || Boolean(oddC.trim()) || Boolean(stakeC.trim());
    if (a === null || b === null || sa === null || sb === null) return null;
    if (a <= 1 || b <= 1 || sa <= 0 || sb <= 0) return null;
    if (needsThirdLeg) {
      if (c === null || sc === null || c <= 1 || sc <= 0) return null;
      return calculateBet(a, b, sa, sb, c, sc);
    }
    return calculateBet(a, b, sa, sb);
  }, [activeSurebet, oddA, oddB, oddC, stakeA, stakeB, stakeC]);

  const filteredHistory = useMemo(() => {
    const searchTerm = historySearch.trim().toLowerCase();
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    const todayDate = new Date(now);
    todayDate.setHours(0, 0, 0, 0);
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(todayDate.getDate() - 1);

    const byFilter = historyItems.filter((item) => {
      const itemDate = new Date(`${item.date}T00:00:00`);
      const matchesDate = historyFilter === 'today'
        ? itemDate.getTime() === todayDate.getTime()
        : historyFilter === 'yesterday'
          ? itemDate.getTime() === yesterdayDate.getTime()
          : historyFilter === 'date'
            ? item.date === selectedDate
            : itemDate >= start && itemDate <= now;
      if (!matchesDate) return false;
      if (historyTypeFilter !== 'all' && item.type !== historyTypeFilter) return false;
      if (!searchTerm) return true;

      return [
        item.eventName,
        item.sport,
        item.competition,
        item.market,
        item.selection,
        item.line == null ? null : String(item.line),
        item.bookmakerA,
        item.directionA,
        item.bookmakerB,
        item.directionB,
        item.bookmakerC,
        item.directionC,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm);
    });

    return byFilter.map((item) => ({
      ...item,
      roi: typeof item.roiPercent === 'number'
        ? item.roiPercent
        : calculateBet(
            item.oddA,
            item.oddB,
            item.suggestedStakeA,
            item.suggestedStakeB,
            item.oddC,
            item.suggestedStakeC,
          ).roi,
    }));
  }, [historyFilter, historyItems, historySearch, historyTypeFilter, selectedDate]);

  const totalHistoryPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE));

  useEffect(() => {
    setHistoryPage(1);
  }, [historyFilter, historySearch, historyTypeFilter, selectedDate]);

  useEffect(() => {
    if (historyPage > totalHistoryPages) {
      setHistoryPage(totalHistoryPages);
    }
  }, [historyPage, totalHistoryPages]);

  const pagedHistory = useMemo(() => {
    const startIndex = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return filteredHistory.slice(startIndex, startIndex + HISTORY_PAGE_SIZE);
  }, [filteredHistory, historyPage]);

  useEffect(() => {
    if (editingTrackedId) return;
    if (pagedHistory.length === 0) {
      if (selectedSurebetId) setSelectedSurebetId('');
      return;
    }
    if (!pagedHistory.some((item) => item.id === selectedSurebetId)) {
      setSelectedSurebetId(pagedHistory[0].id);
    }
  }, [editingTrackedId, pagedHistory, selectedSurebetId]);

  const groupedWeekHistory = useMemo(() => {
    const groups: Record<string, Array<SurebetHistoryItem & { roi: number }>> = {};
    pagedHistory.forEach((item) => {
      groups[item.date] = groups[item.date] || [];
      groups[item.date].push(item);
    });
    return Object.keys(groups)
      .sort((a, b) => (a < b ? 1 : -1))
      .map((date) => ({ date, items: groups[date] }));
  }, [pagedHistory]);

  const typedTracked: TrackedSurebet[] = trackerApiMode ? apiTrackedSurebets : (trackedSurebets as TrackedSurebet[]);
  const typedPayments: Payment[] = trackerApiMode ? apiPayments : (payments as Payment[]);
  const editingTrackedItem = editingTrackedId
    ? typedTracked.find((item) => item.id === editingTrackedId) ?? null
    : null;

  const selectedSummaryRoi = useMemo(() => {
    if (preview) {
      return preview.oddsRoi;
    }
    if (typeof activeSurebet?.roiPercent === 'number') {
      return activeSurebet.roiPercent;
    }
    if (activeSurebet) {
      return calculateBet(
        activeSurebet.oddA,
        activeSurebet.oddB,
        activeSurebet.suggestedStakeA,
        activeSurebet.suggestedStakeB,
        activeSurebet.oddC,
        activeSurebet.suggestedStakeC,
      ).oddsRoi;
    }
    return null;
  }, [activeSurebet, preview]);
  const stats = calculateStats(typedTracked, typedPayments);
  const paymentStats = useMemo(() => {
    const abonnement = typedPayments
      .filter((p) => p.type === 'Abonnement')
      .reduce((sum, p) => sum + p.amount, 0);
    const kosten = typedPayments
      .filter((p) => p.type === 'Kosten')
      .reduce((sum, p) => sum + p.amount, 0);
    const correctie = typedPayments
      .filter((p) => p.type === 'Correctie')
      .reduce((sum, p) => sum + p.amount, 0);
    const netto = abonnement + kosten + correctie;
    return { abonnement, kosten, correctie, netto };
  }, [typedPayments]);

  const renderTrackerLoadingCard = (message: string) => (
    <article className="card tracker-results-note-card">
      <div className="site-loading-block" role="status" aria-live="polite">
        <span className="site-loading-spinner" aria-hidden="true" />
        <p>{message}</p>
      </div>
    </article>
  );

  const renderTrackerErrorCard = () => (
    <article className="card tracker-results-note-card">
      <h3>Data kon niet worden geladen</h3>
      <p>{trackerLoadError?.message || 'Controleer of je bent ingelogd en of de backend bereikbaar is.'}</p>
      <div className="tracker-actions-row">
        <button type="button" className="card-btn tracker-primary-btn" onClick={() => void loadTrackerData()}>
          Opnieuw proberen
        </button>
        {trackerLoadError?.accessDenied && (
          <Link to="/pricing" className="card-btn card-link public-cta-secondary">
            Bekijk abonnementen
          </Link>
        )}
      </div>
    </article>
  );

  const dismissSaveToast = () => {
    if (saveToastTimerRef.current !== null) {
      window.clearTimeout(saveToastTimerRef.current);
      saveToastTimerRef.current = null;
    }
    setToast(null);
  };

  const resetPaymentForm = () => {
    setEditingPaymentId(null);
    setPaymentDate(today);
    setPaymentType('Kosten');
    setPaymentDescription('');
    setPaymentAmount('');
  };

  const triggerToast = (nextToast: TrackerToast) => {
    if (saveToastTimerRef.current !== null) {
      window.clearTimeout(saveToastTimerRef.current);
    }

    setToast(nextToast);
    saveToastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      saveToastTimerRef.current = null;
    }, 4000);
  };

  const handleToastClick = () => {
    if (toast?.action === 'mijn-surebets') {
      setActiveTab('mijn-surebets');
      requestAnimationFrame(() => {
        mySurebetsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    dismissSaveToast();
  };

  const handleSaveTracked = async () => {
    if (!preview) return;
    const existingItem = editingTrackedId
      ? typedTracked.find((item) => item.id === editingTrackedId)
      : null;
    const resolvedSurebetId = activeSurebet?.id ?? existingItem?.surebetId;
    const resolvedEventName = activeSurebet?.eventName ?? existingItem?.eventName;
    const resolvedMarket = activeSurebet?.market ?? existingItem?.market;
    const resolvedType = activeSurebet?.type ?? existingItem?.type;
    if (!resolvedSurebetId || !resolvedEventName || !resolvedMarket || !resolvedType) return;

    const nextId = editingTrackedId ?? `tracked_${Date.now()}`;
    const nextSurebetId = isManualSurebetMode && !editingTrackedId ? nextId : resolvedSurebetId;
    const newItem: TrackedSurebet = {
      id: nextId,
      date: selectedDate,
      surebetId: nextSurebetId,
      eventName: resolvedEventName,
      sport: activeSurebet?.sport ?? existingItem?.sport,
      competition: activeSurebet?.competition ?? existingItem?.competition ?? null,
      market: resolvedMarket,
      selection: activeSurebet?.selection ?? existingItem?.selection ?? null,
      line: activeSurebet?.line ?? existingItem?.line ?? null,
      type: resolvedType,
      bookmakerA: activeSurebet?.bookmakerA ?? existingItem?.bookmakerA,
      directionA: activeSurebet?.directionA ?? existingItem?.directionA,
      oddA: parseNumber(oddA) ?? 0,
      originalOddA: activeSurebet?.oddA ?? existingItem?.originalOddA,
      oddB: parseNumber(oddB) ?? 0,
      originalOddB: activeSurebet?.oddB ?? existingItem?.originalOddB,
      stakeA: parseNumber(stakeA) ?? 0,
      stakeB: parseNumber(stakeB) ?? 0,
      bookmakerB: activeSurebet?.bookmakerB ?? existingItem?.bookmakerB,
      directionB: activeSurebet?.directionB ?? existingItem?.directionB,
      bookmakerC: activeSurebet?.bookmakerC ?? existingItem?.bookmakerC ?? null,
      directionC: activeSurebet?.directionC ?? existingItem?.directionC ?? null,
      oddC: parseNumber(oddC) ?? null,
      originalOddC: activeSurebet?.oddC ?? existingItem?.originalOddC ?? null,
      stakeC: parseNumber(stakeC) ?? null,
      totalStake: preview.totalStake,
      payoutA: preview.payoutA,
      payoutB: preview.payoutB,
      payoutC: preview.payoutC,
      profitA: preview.profitA,
      profitB: preview.profitB,
      profitC: preview.profitC,
      minimumProfit: preview.minimumProfit,
      roi: preview.roi,
      status,
      result,
    };

    if (trackerApiMode) {
      const formState = { selectedDate, status, result, oddA, oddB, oddC, stakeA, stakeB, stakeC };

      try {
        if (editingTrackedId) {
          setIsUpdatingSurebet(true);
          await trackerApi.updateTrackedSurebet(editingTrackedId, mapTrackerEditToApiUpdatePayload(formState));
          await loadTrackerData();
          setEditingTrackedId(null);
          triggerToast({
            title: 'Surebet bijgewerkt',
            message: 'Klik hier om je opgeslagen surebets te bekijken.',
            tone: 'success',
            action: 'mijn-surebets',
          });
        } else {
          if (!activeSurebet) return;
          setIsSavingSurebet(true);
          await trackerApi.createTrackedSurebet(mapTrackerFormToApiCreatePayload(formState, activeSurebet, { manualEntry: isManualSurebetMode }));
          await loadTrackerData();
          triggerToast({
            title: 'Surebet opgeslagen',
            message: 'Klik hier om je opgeslagen surebets te bekijken.',
            tone: 'success',
            action: 'mijn-surebets',
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Tracker surebet mutation failed', error);
        }
        triggerToast({
          title: editingTrackedId ? 'Surebet kon niet worden bijgewerkt' : 'Surebet kon niet worden opgeslagen',
          message: getTrackerApiErrorMessage(
            error,
            editingTrackedId
              ? 'Surebet kon niet worden bijgewerkt. Probeer het opnieuw.'
              : 'Surebet kon niet worden opgeslagen. Probeer het opnieuw.',
          ),
          tone: 'error',
          action: null,
        });
      } finally {
        setIsSavingSurebet(false);
        setIsUpdatingSurebet(false);
      }
      return;
    }

    if (editingTrackedId) {
      setTrackedSurebets(typedTracked.map((item) => (item.id === editingTrackedId ? newItem : item)));
      setEditingTrackedId(null);
      triggerToast({
        title: 'Surebet bijgewerkt',
        message: 'Klik hier om je opgeslagen surebets te bekijken.',
        tone: 'success',
        action: 'mijn-surebets',
      });
      return;
    }
    setTrackedSurebets([newItem, ...typedTracked]);
    triggerToast({
      title: 'Surebet opgeslagen',
      message: 'Klik hier om je opgeslagen surebets te bekijken.',
      tone: 'success',
      action: 'mijn-surebets',
    });
  };

  const handleDeleteTracked = async (id: string) => {
    if (trackerApiMode) {
      if (!window.confirm('Weet je zeker dat je deze surebet wilt verwijderen?')) return;

      try {
        setIsDeletingSurebetId(id);
        await trackerApi.deleteTrackedSurebet(id);
        await loadTrackerData();
        if (editingTrackedId === id) {
          setEditingTrackedId(null);
        }
        triggerToast({
          title: 'Surebet verwijderd',
          message: 'Je overzicht is opnieuw geladen.',
          tone: 'success',
          action: 'mijn-surebets',
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Tracker surebet delete failed', error);
        }
        triggerToast({
          title: 'Surebet kon niet worden verwijderd',
          message: getTrackerApiErrorMessage(error, 'Surebet kon niet worden verwijderd. Probeer het opnieuw.'),
          tone: 'error',
          action: null,
        });
      } finally {
        setIsDeletingSurebetId(null);
      }
      return;
    }

    setTrackedSurebets(typedTracked.filter((item) => item.id !== id));
    if (editingTrackedId === id) {
      setEditingTrackedId(null);
    }
  };

  const handleEditTracked = (item: TrackedSurebet) => {
    setEditingTrackedId(item.id);
    const hasSurebet = historyItems.some((s) => s.id === item.surebetId);
    if (hasSurebet) {
      setIsManualSurebetMode(false);
      setSelectedSurebetId(item.surebetId);
    } else {
      setIsManualSurebetMode(true);
      setSelectedSurebetId('');
      setManualSurebetForm({
        eventName: item.eventName,
        sport: item.sport || 'soccer',
        competition: item.competition || '',
        market: item.market,
        selection: item.selection || '',
        line: item.line == null ? '' : String(item.line),
        type: item.type,
        bookmakerA: item.bookmakerA || '',
        directionA: item.directionA || '',
        bookmakerB: item.bookmakerB || '',
        directionB: item.directionB || '',
        bookmakerC: item.bookmakerC || '',
        directionC: item.directionC || '',
      });
    }
    setSelectedDate(item.date);
    setOddA(String(item.oddA));
    setOddB(String(item.oddB));
    setOddC(item.oddC == null ? '' : String(item.oddC));
    setStakeA(String(item.stakeA));
    setStakeB(String(item.stakeB));
    setStakeC(item.stakeC == null ? '' : String(item.stakeC));
    const editableResult = toEditableBetResult(item.result);
    setResult(editableResult);
    setStatus(getStatusForResult(editableResult));
    setActiveTab('toevoegen');
  };

  const handleResultChange = (nextResult: BetResult) => {
    setResult(nextResult);
    setStatus(getStatusForResult(nextResult));
  };

  const handleSavePayment = async () => {
    const amount = parseNumber(paymentAmount);
    if (amount === null || amount === 0) return;

    const formState = { paymentDate, paymentType, paymentDescription, paymentAmount };

    if (editingPaymentId && trackerApiMode) {
      try {
        setIsUpdatingCost(true);
        await trackerApi.updateTrackerCost(editingPaymentId, mapCostEditToApiUpdatePayload(formState));
        await loadTrackerData();
        resetPaymentForm();
        triggerToast({
          title: 'Kostenpost bijgewerkt',
          message: 'Je kostenoverzicht is bijgewerkt.',
          tone: 'success',
          action: null,
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Tracker cost update failed', error);
        }
        triggerToast({
          title: 'Kostenpost kon niet worden bijgewerkt',
          message: getTrackerApiErrorMessage(error, 'Kostenpost kon niet worden bijgewerkt.'),
          tone: 'error',
          action: null,
        });
      } finally {
        setIsUpdatingCost(false);
      }
      return;
    }

    if (editingPaymentId) {
      setPayments(
        typedPayments.map((payment) => (
          payment.id === editingPaymentId
            ? {
                ...payment,
                date: paymentDate,
                type: paymentType,
                description: paymentDescription.trim(),
                amount: normalizeCostAmount(paymentType, amount),
              }
            : payment
        )),
      );
      resetPaymentForm();
      triggerToast({
        title: 'Kostenpost bijgewerkt',
        message: 'Je kostenoverzicht is bijgewerkt.',
        tone: 'success',
        action: null,
      });
      return;
    }

    if (trackerApiMode) {
      try {
        setIsSavingCost(true);
        await trackerApi.createTrackerCost(mapCostFormToApiCreatePayload(formState));
        await loadTrackerData();
        resetPaymentForm();
        triggerToast({
          title: 'Kostenpost opgeslagen',
          message: 'Je kostenoverzicht is bijgewerkt.',
          tone: 'success',
          action: null,
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Tracker cost create failed', error);
        }
        triggerToast({
          title: 'Kostenpost kon niet worden opgeslagen',
          message: getTrackerApiErrorMessage(error, 'Kostenpost kon niet worden opgeslagen.'),
          tone: 'error',
          action: null,
        });
      } finally {
        setIsSavingCost(false);
      }
      return;
    }

    const newPayment: Payment = {
      id: `payment_${Date.now()}`,
      date: paymentDate,
      type: paymentType,
      description: paymentDescription.trim(),
      amount: normalizeCostAmount(paymentType, amount),
    };
    setPayments([newPayment, ...typedPayments]);
    resetPaymentForm();
  };

  const handleEditPayment = (payment: Payment) => {
    if (payment.automatic) return;
    setEditingPaymentId(payment.id);
    setPaymentDate(payment.date);
    setPaymentType(payment.type);
    setPaymentDescription(payment.description);
    setPaymentAmount(String(payment.amount));
  };

  const handleCancelEditPayment = () => {
    resetPaymentForm();
  };

  const handleDeletePayment = async (id: string) => {
    if (typedPayments.find((payment) => payment.id === id)?.automatic) return;
    if (trackerApiMode) {
      if (!window.confirm('Weet je zeker dat je deze kostenpost wilt verwijderen?')) return;

      try {
        setIsDeletingCostId(id);
        await trackerApi.deleteTrackerCost(id);
        await loadTrackerData();
        triggerToast({
          title: 'Kostenpost verwijderd',
          message: 'Je kostenoverzicht is bijgewerkt.',
          tone: 'success',
          action: null,
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Tracker cost delete failed', error);
        }
        triggerToast({
          title: 'Kostenpost kon niet worden verwijderd',
          message: getTrackerApiErrorMessage(error, 'Kostenpost kon niet worden verwijderd.'),
          tone: 'error',
          action: null,
        });
      } finally {
        setIsDeletingCostId(null);
      }
      return;
    }

    setPayments(typedPayments.filter((p) => p.id !== id));
  };

  const handleSelectSurebet = (item: SurebetHistoryItem) => {
    setIsManualSurebetMode(false);
    setSelectedSurebetId(item.id);
    setSelectedDate(item.date);
  };

  const updateManualSurebetForm = (field: keyof ManualSurebetForm, value: string) => {
    setManualSurebetForm((current) => ({ ...current, [field]: value }));
  };

  const toggleManualSurebetMode = () => {
    setIsManualSurebetMode((current) => {
      const next = !current;
      if (next) {
        setSelectedSurebetId('');
        setSelectedDate(today);
        setOddA('');
        setOddB('');
        setOddC('');
        setStakeA('');
        setStakeB('');
        setStakeC('');
        setResult('Nog niet bekend');
        setStatus('Open');
      }
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilterSearch('');
    setFilterStatus('');
    setFilterType('');
    setFilterResult('');
    setFilterPeriod('alles');
    setFilterSort('nieuwste');
    setTrackedPage(1);
  };

  const filteredTracked = useMemo(() => {
    const now = new Date();
    const todayStr = toInputDate(now);
    const yesterdayStr = toInputDate(new Date(Date.now() - 86400000));
    const thisYear = now.getFullYear();
    const thisMonth = now.getMonth();
    const prevMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const prevMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    let result = typedTracked.filter((item) => {
      // Search
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const fields = [
          item.eventName,
          item.market,
          formatTrackerMarket(item),
          item.selection,
          item.line == null ? '' : String(item.line),
          item.status,
          item.result,
        ].map((v) =>
          (v ?? '').toLowerCase(),
        );
        if (!fields.some((f) => f.includes(q))) return false;
      }
      // Status
      if (filterStatus && item.status !== filterStatus) return false;
      // Type
      if (filterType && item.type !== filterType) return false;
      // Result
      if (filterResult && item.result !== filterResult) return false;
      // Period
      if (filterPeriod !== 'alles') {
        const d = new Date(`${item.date}T00:00:00`);
        if (filterPeriod === 'vandaag' && item.date !== todayStr) return false;
        if (filterPeriod === 'gisteren' && item.date !== yesterdayStr) return false;
        if (filterPeriod === 'week7') {
          const cutoff = new Date(now.getTime() - 6 * 86400000);
          cutoff.setHours(0, 0, 0, 0);
          if (d < cutoff) return false;
        }
        if (filterPeriod === 'deze-maand') {
          if (d.getFullYear() !== thisYear || d.getMonth() !== thisMonth) return false;
        }
        if (filterPeriod === 'vorige-maand') {
          if (d.getFullYear() !== prevMonthYear || d.getMonth() !== prevMonth) return false;
        }
      }
      return true;
    });

    result = [...result].sort((a, b) => {
      if (filterSort === 'nieuwste') return a.date < b.date ? 1 : -1;
      if (filterSort === 'oudste') return a.date > b.date ? 1 : -1;
      if (filterSort === 'hoogste-resultaat') return getFinalResult(b) - getFinalResult(a);
      if (filterSort === 'laagste-resultaat') return getFinalResult(a) - getFinalResult(b);
      if (filterSort === 'hoogste-roi') return getFinalRoi(b) - getFinalRoi(a);
      if (filterSort === 'laagste-roi') return getFinalRoi(a) - getFinalRoi(b);
      if (filterSort === 'hoogste-inzet') return (b.totalStake ?? 0) - (a.totalStake ?? 0);
      if (filterSort === 'laagste-inzet') return (a.totalStake ?? 0) - (b.totalStake ?? 0);
      return 0;
    });

    return result;
  }, [typedTracked, filterSearch, filterStatus, filterType, filterResult, filterPeriod, filterSort]);

  const totalTrackedPages = Math.max(1, Math.ceil(filteredTracked.length / TRACKED_SUREBETS_PAGE_SIZE));

  useEffect(() => {
    setTrackedPage(1);
  }, [filterSearch, filterStatus, filterType, filterResult, filterPeriod, filterSort]);

  useEffect(() => {
    if (trackedPage > totalTrackedPages) {
      setTrackedPage(totalTrackedPages);
    }
  }, [trackedPage, totalTrackedPages]);

  const pagedTracked = useMemo(() => {
    const startIndex = (trackedPage - 1) * TRACKED_SUREBETS_PAGE_SIZE;
    return filteredTracked.slice(startIndex, startIndex + TRACKED_SUREBETS_PAGE_SIZE);
  }, [filteredTracked, trackedPage]);

  const handleExcelExport = () => {
    const headers = ['Datum', 'Wedstrijd', 'Markt', 'Type', 'Odd A', 'Inzet A', 'Odd B', 'Inzet B', 'Totale inzet', 'Resultaat (€)', 'ROI (%)', 'Status'];
    const rows = filteredTracked.map((item) => {
      const finalResult = getFinalResult(item);
      const finalRoi = item.status === 'Open' || item.result === 'Nog niet bekend'
        ? 0
        : item.totalStake > 0 ? (finalResult / item.totalStake) * 100 : 0;
      return [
        item.date,
        item.eventName,
        formatTrackerMarket(item),
        item.type,
        item.oddA,
        item.stakeA,
        item.oddB,
        item.stakeB,
        item.totalStake,
        round2(finalResult),
        round2(finalRoi),
        item.status,
      ];
    });
    const costRows = typedPayments.map((payment) => [
      payment.date,
      payment.type,
      payment.description,
      payment.amount,
      payment.automatic ? 'Automatisch via Stripe' : 'Handmatig',
    ]);
    const workbook = createExcelWorkbook([
      { name: 'Surebets', rows: [headers, ...rows] },
      { name: 'Kosten', rows: [['Datum', 'Type', 'Omschrijving', 'Bedrag (€)', 'Bron'], ...costRows] },
    ]);
    downloadBlob(workbook, `perfectodds-tracker-${toAmsterdamInputDate(new Date())}.xlsx`);
  };

  return (
    <PublicLayout>
      <>
        <section className="public-page-card subscription-page tracker-page tracker-page-modern tracker-refresh">
          <section className="tracker-main-header card">
            <div>
              <span className="tracker-refresh-kicker">Persoonlijke tracker</span>
              <h1>Mijn tracker</h1>
              <p>Voeg geplaatste surebets toe, bekijk je resultaten en houd je kosten overzichtelijk bij.</p>
            </div>
            <div className="tracker-main-header-tools">
              <Link to="/tracker/surebet-history" className="card-btn card-link tracker-header-link">Surebet archief</Link>
              <span className="tracker-env-badge">
                {process.env.NODE_ENV !== 'production' && trackerApiMode ? `Tracker mode: ${TRACKER_MODE.toUpperCase()}` : 'Betaalde omgeving'}
              </span>
            </div>
          </section>

          <section className="subscription-section tracker-section">
          <div className="tracker-tab-shell card">
            <div className="tracker-tab-row" role="tablist" aria-label="Tracker tabs">
              <button
                type="button"
                className={`tracker-tab-pill ${activeTab === 'toevoegen' ? 'tracker-tab-pill-active' : ''}`}
                onClick={() => setActiveTab('toevoegen')}
              >
                Toevoegen
              </button>
              <button
                type="button"
                className={`tracker-tab-pill ${activeTab === 'resultaten' ? 'tracker-tab-pill-active' : ''}`}
                onClick={() => setActiveTab('resultaten')}
              >
                Resultaten
              </button>
              <button
                type="button"
                className={`tracker-tab-pill ${activeTab === 'mijn-surebets' ? 'tracker-tab-pill-active' : ''}`}
                onClick={() => setActiveTab('mijn-surebets')}
              >
                Mijn surebets
              </button>
              <button
                type="button"
                className={`tracker-tab-pill ${activeTab === 'kosten' ? 'tracker-tab-pill-active' : ''}`}
                onClick={() => setActiveTab('kosten')}
              >
                Kosten
              </button>
            </div>
          </div>

          {activeTab === 'toevoegen' && (
            <div className="tracker-add-stack">
              {trackerNeedsBackendLoad && trackerLoadState === 'loading' && renderTrackerLoadingCard('Surebet history laden...')}
              {trackerNeedsBackendLoad && trackerLoadState === 'error' && renderTrackerErrorCard()}
              {(!trackerNeedsBackendLoad || trackerLoadState === 'loaded') && (
                <>
              <article className="card tracker-step-card tracker-pick-card">
                <div className="tracker-step-card-header">
                  <div>
                    <h3>1. Kies een surebet</h3>
                    <p className="tracker-muted">
                      {isManualSurebetMode
                        ? 'Vul zelf de surebetgegevens in en sla hem daarna op in je tracker.'
                        : 'Kies eerst een periode of datum en selecteer daarna de surebet die je wilt opslaan.'}
                    </p>
                  </div>
                  <button type="button" className="tracker-manual-toggle-btn" onClick={toggleManualSurebetMode}>
                    {isManualSurebetMode ? 'Kies uit archief' : 'Voeg je eigen surebet toe'}
                  </button>
                </div>

                {isManualSurebetMode ? (
                  <div className="tracker-manual-surebet-panel">
                    <div className="tracker-form-two-col tracker-manual-form-grid">
                      <div className="tracker-field tracker-field-wide">
                        <label>Match</label>
                        <input value={manualSurebetForm.eventName} onChange={(e) => updateManualSurebetForm('eventName', e.target.value)} placeholder="Bijv. PSV - Ajax" />
                      </div>
                      <div className="tracker-field">
                        <label>Type</label>
                        <select value={manualSurebetForm.type} onChange={(e) => updateManualSurebetForm('type', e.target.value as BetType)}>
                          <option value="Pre-match">Pre-match</option>
                          <option value="Live">Live</option>
                        </select>
                      </div>
                      <div className="tracker-field">
                        <label>Sport</label>
                        <input value={manualSurebetForm.sport} onChange={(e) => updateManualSurebetForm('sport', e.target.value)} placeholder="soccer" />
                      </div>
                      <div className="tracker-field">
                        <label>Competitie</label>
                        <input value={manualSurebetForm.competition} onChange={(e) => updateManualSurebetForm('competition', e.target.value)} placeholder="Optioneel" />
                      </div>
                      <div className="tracker-field">
                        <label>Markt</label>
                        <select value={manualSurebetForm.market} onChange={(e) => updateManualSurebetForm('market', e.target.value)}>
                          {MANUAL_MARKET_OPTIONS.map((market) => (
                            <option key={market.value} value={market.value}>{market.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="tracker-field">
                        <label>Selectie</label>
                        <input value={manualSurebetForm.selection} onChange={(e) => updateManualSurebetForm('selection', e.target.value)} placeholder="Bijv. Both, PSV, spelernaam" />
                      </div>
                      <div className="tracker-field">
                        <label>Line</label>
                        <input value={manualSurebetForm.line} onChange={(e) => updateManualSurebetForm('line', e.target.value)} placeholder="Bijv. 2,5" />
                      </div>
                      <div className="tracker-field">
                        <label>Bookmaker A</label>
                        <input value={manualSurebetForm.bookmakerA} onChange={(e) => updateManualSurebetForm('bookmakerA', e.target.value)} placeholder="Bijv. Bet365" />
                      </div>
                      <div className="tracker-field">
                        <label>Uitkomst A</label>
                        <input value={manualSurebetForm.directionA} onChange={(e) => updateManualSurebetForm('directionA', e.target.value)} placeholder="Bijv. Over" />
                      </div>
                      <div className="tracker-field">
                        <label>Bookmaker B</label>
                        <input value={manualSurebetForm.bookmakerB} onChange={(e) => updateManualSurebetForm('bookmakerB', e.target.value)} placeholder="Bijv. BetCity" />
                      </div>
                      <div className="tracker-field">
                        <label>Uitkomst B</label>
                        <input value={manualSurebetForm.directionB} onChange={(e) => updateManualSurebetForm('directionB', e.target.value)} placeholder="Bijv. Under" />
                      </div>
                      <div className="tracker-field">
                        <label>Bookmaker C</label>
                        <input value={manualSurebetForm.bookmakerC} onChange={(e) => updateManualSurebetForm('bookmakerC', e.target.value)} placeholder="Optioneel" />
                      </div>
                      <div className="tracker-field">
                        <label>Uitkomst C</label>
                        <input value={manualSurebetForm.directionC} onChange={(e) => updateManualSurebetForm('directionC', e.target.value)} placeholder="Optioneel" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="tracker-pick-filterbar" role="tablist" aria-label="Surebet periode filter">
                  <button
                    type="button"
                    className={`tracker-mini-pill ${historyFilter === 'today' ? 'tracker-mini-pill-active' : ''}`}
                    onClick={() => setHistoryFilter('today')}
                  >
                    Vandaag
                  </button>
                  <button
                    type="button"
                    className={`tracker-mini-pill ${historyFilter === 'yesterday' ? 'tracker-mini-pill-active' : ''}`}
                    onClick={() => setHistoryFilter('yesterday')}
                  >
                    Gisteren
                  </button>
                  <button
                    type="button"
                    className={`tracker-mini-pill ${historyFilter === 'week' ? 'tracker-mini-pill-active' : ''}`}
                    onClick={() => setHistoryFilter('week')}
                  >
                    Deze week
                  </button>
                  <button
                    type="button"
                    className={`tracker-mini-pill ${historyFilter === 'date' ? 'tracker-mini-pill-active' : ''}`}
                    onClick={() => setHistoryFilter('date')}
                  >
                    Kies datum
                  </button>
                </div>

                {historyFilter === 'date' && (
                  <div className="tracker-pick-date">
                    <DatePickerField label="Kies datum" value={selectedDate} onChange={setSelectedDate} />
                  </div>
                )}

                <div className="tracker-pick-controls">
                  <div className="tracker-field">
                    <label>Zoek speler of wedstrijd</label>
                    <input
                      type="search"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Team, speler, competitie of bookmaker"
                    />
                  </div>
                  <div className="tracker-field">
                    <label>Type</label>
                    <select value={historyTypeFilter} onChange={(e) => setHistoryTypeFilter(e.target.value as HistoryTypeFilter)}>
                      <option value="all">Live en pre-match</option>
                      <option value="Live">Live</option>
                      <option value="Pre-match">Pre-match</option>
                    </select>
                  </div>
                </div>

                {historyFilter === 'week' ? (
                  groupedWeekHistory.length > 0 ? (
                    <div className="tracker-pick-week-groups">
                      {groupedWeekHistory.map((group) => (
                        <div key={group.date} className="tracker-pick-week-group">
                          <h4>{formatDateLabel(group.date)}</h4>
                          <div className="tracker-surebet-grid">
                            {group.items.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                className={`tracker-surebet-card ${selectedSurebetId === item.id ? 'is-selected' : ''}`}
                                onClick={() => handleSelectSurebet(item)}
                              >
                                <p className="tracker-surebet-title">{item.eventName}</p>
                                <p className="tracker-surebet-meta"><span>ROI</span><strong>{formatPercent(item.roi)}</strong></p>
                                <p className="tracker-surebet-market">{formatTrackerMarket(item)}</p>
                                <p className="tracker-surebet-type">{item.type}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="tracker-empty-state-modern">
                      <h4>Geen surebets gevonden</h4>
                      <p>Er zijn geen surebets beschikbaar voor deze selectie.</p>
                    </div>
                  )
                ) : pagedHistory.length > 0 ? (
                  <div className="tracker-surebet-grid">
                    {pagedHistory.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`tracker-surebet-card ${selectedSurebetId === item.id ? 'is-selected' : ''}`}
                        onClick={() => handleSelectSurebet(item)}
                      >
                        <p className="tracker-surebet-title">{item.eventName}</p>
                        <p className="tracker-surebet-meta"><span>ROI</span><strong>{formatPercent(item.roi)}</strong></p>
                        <p className="tracker-surebet-market">{formatTrackerMarket(item)}</p>
                        <p className="tracker-surebet-type">{item.type}</p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="tracker-empty-state-modern">
                    <h4>Geen surebets gevonden</h4>
                    <p>Er zijn geen surebets beschikbaar voor deze selectie.</p>
                  </div>
                )}

                {filteredHistory.length > HISTORY_PAGE_SIZE && (
                  <div className="tracker-pagination" aria-label="Surebet pagina navigatie">
                    <button
                      type="button"
                      className="tracker-page-btn"
                      onClick={() => setHistoryPage((current) => Math.max(1, current - 1))}
                      disabled={historyPage === 1}
                    >
                      Vorige
                    </button>
                    <div className="tracker-page-list">
                      {Array.from({ length: totalHistoryPages }, (_, index) => index + 1).map((pageNumber) => (
                        <button
                          key={pageNumber}
                          type="button"
                          className={`tracker-page-btn ${historyPage === pageNumber ? 'tracker-page-btn-active' : ''}`}
                          onClick={() => setHistoryPage(pageNumber)}
                          aria-current={historyPage === pageNumber ? 'page' : undefined}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="tracker-page-btn"
                      onClick={() => setHistoryPage((current) => Math.min(totalHistoryPages, current + 1))}
                      disabled={historyPage === totalHistoryPages}
                    >
                      Volgende
                    </button>
                  </div>
                )}
                  </>
                )}
              </article>

              <div className="tracker-add-layout">
                <div className="tracker-add-main-col">
                  <article className="card tracker-step-card">
                    <h3>2. Vul je eigen gegevens in</h3>
                    {activeSurebet ? (
                      <p className="tracker-picked-summary">
                        {isManualSurebetMode ? 'Eigen surebet' : 'Gekozen surebet'}: {activeSurebet.eventName} · {formatTrackerMarket(activeSurebet)}{selectedSummaryRoi !== null ? ` · ${formatPercent(selectedSummaryRoi)}` : ''}
                      </p>
                    ) : editingTrackedItem ? (
                      <p className="tracker-picked-summary">
                        Je bewerkt: {editingTrackedItem.eventName} · {formatTrackerMarket(editingTrackedItem)}
                      </p>
                    ) : (
                      <p className="tracker-muted">Selecteer hierboven een surebet of voeg je eigen surebet toe om je gegevens in te vullen.</p>
                    )}

                    <div className="tracker-form-two-col">
                      <div className="tracker-field"><label>Odd A geplaatst</label><input value={oddA} onChange={(e) => setOddA(e.target.value)} /></div>
                      <div className="tracker-field"><label>Inzet A</label><input value={stakeA} onChange={(e) => setStakeA(e.target.value)} placeholder="Bijv. 50" /></div>
                      <div className="tracker-field"><label>Odd B geplaatst</label><input value={oddB} onChange={(e) => setOddB(e.target.value)} /></div>
                      <div className="tracker-field"><label>Inzet B</label><input value={stakeB} onChange={(e) => setStakeB(e.target.value)} placeholder="Bijv. 50" /></div>
                      {(activeSurebet?.oddC != null || editingTrackedItem?.oddC != null || oddC.trim() || stakeC.trim() || manualSurebetForm.bookmakerC.trim() || manualSurebetForm.directionC.trim()) && (
                        <>
                          <div className="tracker-field"><label>Odd C geplaatst</label><input value={oddC} onChange={(e) => setOddC(e.target.value)} /></div>
                          <div className="tracker-field"><label>Inzet C</label><input value={stakeC} onChange={(e) => setStakeC(e.target.value)} placeholder="Bijv. 25" /></div>
                        </>
                      )}
                      <div className="tracker-field">
                        <label>Resultaat</label>
                        <select value={result} onChange={(e) => handleResultChange(e.target.value as BetResult)}>
                          <option value="Nog niet bekend">Nog niet bekend</option>
                          <option value="A gewonnen">A gewonnen</option>
                          <option value="B gewonnen">B gewonnen</option>
                          <option value="Void">Void</option>
                        </select>
                      </div>
                      <div className="tracker-field">
                        <label>Status</label>
                        <div className="tracker-status-display" aria-live="polite">{status}</div>
                      </div>
                    </div>

                    <div className="tracker-actions-row">
                      <button
                        type="button"
                        className="card-btn tracker-primary-btn"
                        onClick={handleSaveTracked}
                        disabled={!preview || !activeSurebet || isSavingSurebet || isUpdatingSurebet}
                        aria-busy={isSavingSurebet || isUpdatingSurebet}
                      >
                        {editingTrackedId
                          ? isUpdatingSurebet
                            ? 'Opslaan...'
                            : 'Wijziging opslaan'
                          : isSavingSurebet
                            ? 'Opslaan...'
                            : 'Opslaan'}
                      </button>
                    </div>
                  </article>
                </div>

                <div className="tracker-add-side-col">
                  <article className="card tracker-calc-card">
                    <h3>Berekening</h3>
                    {preview ? (
                      <div className="tracker-calc-list">
                        <p><span>Totale inzet</span><strong>{formatCurrency(preview.totalStake)}</strong></p>
                        <p><span>Uitbetaling A</span><strong>{formatCurrency(preview.payoutA)}</strong></p>
                        <p><span>Uitbetaling B</span><strong>{formatCurrency(preview.payoutB)}</strong></p>
                        <p><span>Winst A</span><strong>{formatCurrency(preview.profitA)}</strong></p>
                        <p><span>Winst B</span><strong>{formatCurrency(preview.profitB)}</strong></p>
                        <p><span>ROI%</span><strong>{formatPercent(preview.oddsRoi)}</strong></p>
                      </div>
                    ) : (
                      <p className="tracker-muted">Vul geldige odds en inzetten in om de berekening te zien.</p>
                    )}
                  </article>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'resultaten' && (
            <section className="tracker-results-pane">
              <div className="tracker-section-header">
                <h2>Resultaten</h2>
                <p>Bekijk je surebetwinst, ROI, openstaande bets en netto resultaat.</p>
              </div>

              {trackerApiMode && trackerLoadState === 'loading' && renderTrackerLoadingCard('Resultaten laden...')}
              {trackerApiMode && trackerLoadState === 'error' && renderTrackerErrorCard()}

              {(!trackerApiMode || trackerLoadState === 'loaded') && (
                <>
                  <div className="tracker-stat-grid tracker-stat-grid-6">
                    <article className="tracker-stat-card card"><h3>Totale surebetwinst</h3><p>{formatCurrency(stats.totalSurebetWinst)}</p></article>
                    <article className="tracker-stat-card card"><h3>Totaal ingezet</h3><p>{formatCurrency(stats.totalStake)}</p></article>
                    <article className="tracker-stat-card card"><h3>Gemiddelde ROI</h3><p>{formatPercent(stats.avgRoi)}</p></article>
                    <article className="tracker-stat-card card"><h3>Open surebets</h3><p>{stats.openCount}</p></article>
                    <article className="tracker-stat-card card"><h3>Afgeronde surebets</h3><p>{stats.completedCount}</p></article>
                    <article className="tracker-stat-card card"><h3>Netto resultaat</h3><p>{formatCurrency(stats.netResult)}</p></article>
                  </div>

                  <article className="card tracker-results-note-card">
                    <p>Open surebets tellen nog niet mee als definitief resultaat. Zodra je een resultaat invult, wordt je surebetwinst bijgewerkt.</p>
                  </article>
                </>
              )}
            </section>
          )}

          {activeTab === 'mijn-surebets' && (
            <section ref={mySurebetsSectionRef} id="mijn-surebets" className="tracker-surebets-pane">
              <div className="tracker-section-header">
                <h2>Mijn surebets</h2>
                <p>Bekijk, filter en beheer al je geplaatste surebets.</p>
              </div>

              {trackerApiMode && trackerLoadState === 'loading' && renderTrackerLoadingCard('Surebets laden...')}
              {trackerApiMode && trackerLoadState === 'error' && renderTrackerErrorCard()}

              {/* Filter card */}
              {(!trackerApiMode || trackerLoadState === 'loaded') && (
              <>
              <div className="tracker-filter-card card">
                <div className="tracker-filter-card-header">
                  <div>
                    <h3 className="tracker-filter-title">Filter je surebets</h3>
                    <p className="tracker-filter-subtitle">Zoek, filter en sorteer je geplaatste surebets.</p>
                  </div>
                  <div className="tracker-filter-actions">
                    <button type="button" className="tracker-filter-reset-btn" onClick={handleResetFilters}>Filters resetten</button>
                    <button type="button" className="tracker-filter-export-btn card-btn" onClick={handleExcelExport}>Excel export</button>
                  </div>
                </div>
                <div className="tracker-filter-grid">
                  <div className="tracker-filter-field tracker-filter-search">
                    <label htmlFor="filter-search">Zoeken</label>
                    <input
                      id="filter-search"
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      placeholder="Zoek op wedstrijd, markt, status..."
                    />
                  </div>
                  <div className="tracker-filter-field">
                    <label htmlFor="filter-status">Status</label>
                    <select id="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                      <option value="">Alle statussen</option>
                      <option value="Open">Open</option>
                      <option value="Afgerond">Afgerond</option>
                    </select>
                  </div>
                  <div className="tracker-filter-field">
                    <label htmlFor="filter-type">Type</label>
                    <select id="filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                      <option value="">Alle types</option>
                      <option value="Live">Live</option>
                      <option value="Pre-match">Pre-match</option>
                    </select>
                  </div>
                  <div className="tracker-filter-field">
                    <label htmlFor="filter-result">Resultaat</label>
                    <select id="filter-result" value={filterResult} onChange={(e) => setFilterResult(e.target.value)}>
                      <option value="">Alle resultaten</option>
                      <option value="Nog niet bekend">Nog niet bekend</option>
                      <option value="A gewonnen">A gewonnen</option>
                      <option value="B gewonnen">B gewonnen</option>
                      <option value="Void">Void</option>
                    </select>
                  </div>
                  <div className="tracker-filter-field">
                    <label htmlFor="filter-period">Periode</label>
                    <select id="filter-period" value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value as PeriodFilter)}>
                      <option value="alles">Alles</option>
                      <option value="vandaag">Vandaag</option>
                      <option value="gisteren">Gisteren</option>
                      <option value="week7">Laatste 7 dagen</option>
                      <option value="deze-maand">Deze maand</option>
                      <option value="vorige-maand">Vorige maand</option>
                    </select>
                  </div>
                  <div className="tracker-filter-field">
                    <label htmlFor="filter-sort">Sorteren</label>
                    <select id="filter-sort" value={filterSort} onChange={(e) => setFilterSort(e.target.value as SurebetSortOption)}>
                      <option value="nieuwste">Nieuwste eerst</option>
                      <option value="oudste">Oudste eerst</option>
                      <option value="hoogste-resultaat">Hoogste resultaat</option>
                      <option value="laagste-resultaat">Laagste resultaat</option>
                      <option value="hoogste-roi">Hoogste ROI</option>
                      <option value="laagste-roi">Laagste ROI</option>
                      <option value="hoogste-inzet">Hoogste inzet</option>
                      <option value="laagste-inzet">Laagste inzet</option>
                    </select>
                  </div>
                </div>
                <div className="tracker-filter-summary">
                  {typedTracked.length === 0
                    ? 'Nog geen surebets opgeslagen.'
                    : `${filteredTracked.length} van ${typedTracked.length} surebet${typedTracked.length !== 1 ? 's' : ''} getoond`}
                </div>
              </div>

              <div className="tracker-mobile-tracked-list">
                {typedTracked.length === 0 ? (
                  <article className="tracker-mobile-tracked-card tracker-mobile-tracked-empty">
                    <strong>Nog geen surebets opgeslagen</strong>
                    <p>{trackerApiMode ? 'Sla je eerste surebet op om je overzicht op te bouwen.' : 'Voeg eerst een surebet toe via de tab Toevoegen.'}</p>
                    <button type="button" className="card-btn tracker-primary-btn" onClick={() => setActiveTab('toevoegen')}>Surebet toevoegen</button>
                  </article>
                ) : filteredTracked.length === 0 ? (
                  <article className="tracker-mobile-tracked-card tracker-mobile-tracked-empty">
                    <strong>Geen surebets gevonden</strong>
                    <p>Pas je filters aan of reset de filters om alle surebets te bekijken.</p>
                    <button type="button" className="tracker-filter-reset-btn" onClick={handleResetFilters}>Filters resetten</button>
                  </article>
                ) : (
                  pagedTracked.map((item) => {
                    const safeOddA = Number(item.oddA ?? 0);
                    const safeOddB = Number(item.oddB ?? 0);
                    const safeStakeA = Number(item.stakeA ?? 0);
                    const safeStakeB = Number(item.stakeB ?? 0);
                    const safeTotalStake = Number(item.totalStake ?? 0);
                    const finalResult = getFinalResult(item);
                    const finalRoi = getFinalRoi(item);
                    const finalResultTone = finalResult > 0 ? 'is-positive' : finalResult < 0 ? 'is-negative' : 'is-zero';
                    return (
                      <article key={item.id} className="tracker-mobile-tracked-card">
                        <div className="tracker-mobile-tracked-topline">
                          <div>
                            <span>{item.date}</span>
                            <strong>{item.eventName}</strong>
                          </div>
                          <span className={`tracker-badge ${item.type === 'Live' ? 'type-live' : 'type-pre-match'}`}>{item.type}</span>
                        </div>
                        <p className="tracker-mobile-tracked-market">{formatTrackerMarket(item)}</p>
                        <div className="tracker-mobile-tracked-legs">
                          <div><span>{item.bookmakerA || 'Bookmaker A'}</span><strong>{item.directionA || 'A'} · {safeOddA.toFixed(2)}</strong><small>{formatCurrency(safeStakeA)}</small></div>
                          <div><span>{item.bookmakerB || 'Bookmaker B'}</span><strong>{item.directionB || 'B'} · {safeOddB.toFixed(2)}</strong><small>{formatCurrency(safeStakeB)}</small></div>
                        </div>
                        <div className="tracker-mobile-tracked-summary">
                          <div><span>Inzet</span><strong>{formatCurrency(safeTotalStake)}</strong></div>
                          <div><span>Resultaat</span><strong className={`tracker-mobile-result-value ${finalResultTone}`}>{formatCurrency(finalResult)}</strong></div>
                          <div><span>ROI</span><strong>{formatPercent(finalRoi)}</strong></div>
                          <div><span>Status</span><strong>{item.status}</strong></div>
                        </div>
                        <div className="tracker-mobile-tracked-actions">
                          <button
                            type="button"
                            className="tracker-edit-btn"
                            onClick={() => handleEditTracked(item)}
                            disabled={isSavingSurebet || isUpdatingSurebet || isDeletingSurebetId === item.id}
                          >
                            Wijzigen
                          </button>
                          <button
                            type="button"
                            className="tracker-danger-btn"
                            onClick={() => handleDeleteTracked(item.id)}
                            disabled={isDeletingSurebetId === item.id}
                            aria-busy={isDeletingSurebetId === item.id}
                          >
                            {isDeletingSurebetId === item.id ? 'Verwijderen...' : 'Verwijderen'}
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              {filteredTracked.length > TRACKED_SUREBETS_PAGE_SIZE && (
                <div className="tracker-pagination" aria-label="Mijn surebets pagina navigatie">
                  <button
                    type="button"
                    className="tracker-page-btn"
                    onClick={() => setTrackedPage((current) => Math.max(1, current - 1))}
                    disabled={trackedPage === 1}
                  >
                    Vorige
                  </button>
                  <div className="tracker-page-list">
                    {Array.from({ length: totalTrackedPages }, (_, index) => index + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`tracker-page-btn ${trackedPage === pageNumber ? 'tracker-page-btn-active' : ''}`}
                        onClick={() => setTrackedPage(pageNumber)}
                        aria-current={trackedPage === pageNumber ? 'page' : undefined}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="tracker-page-btn"
                    onClick={() => setTrackedPage((current) => Math.min(totalTrackedPages, current + 1))}
                    disabled={trackedPage === totalTrackedPages}
                  >
                    Volgende
                  </button>
                </div>
              )}
              </>
              )}
            </section>
          )}

          {activeTab === 'kosten' && (
            <section className="tracker-costs-pane">
              <div className="tracker-section-header">
                <h2>Kosten</h2>
                <p>Houd abonnementskosten, overige kosten en correcties bij voor je netto resultaat.</p>
              </div>

              {trackerApiMode && trackerLoadState === 'loading' && renderTrackerLoadingCard('Kosten laden...')}
              {trackerApiMode && trackerLoadState === 'error' && renderTrackerErrorCard()}

              {(!trackerApiMode || trackerLoadState === 'loaded') && (
              <>
              <div className="tracker-costs-top">
                <article className="card tracker-step-card">
                  <h3>{editingPaymentId ? 'Kostenpost bewerken' : 'Nieuwe kostenpost'}</h3>
                  <div className="tracker-form-two-col">
                    <DatePickerField label="Datum" value={paymentDate} onChange={setPaymentDate} />
                    <div className="tracker-field">
                      <label>Type</label>
                      <select value={paymentType} onChange={(e) => setPaymentType(e.target.value as PaymentType)}>
                        <option value="Abonnement">Abonnement</option>
                        <option value="Kosten">Kosten</option>
                        <option value="Correctie">Correctie</option>
                      </select>
                    </div>
                    <div className="tracker-field"><label>Omschrijving</label><input value={paymentDescription} onChange={(e) => setPaymentDescription(e.target.value)} /></div>
                    <div className="tracker-field">
                      <label>Bedrag</label>
                      <input value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="Bijv. 64,95" />
                      <small>{paymentType === 'Correctie' ? 'Correcties mogen positief of negatief zijn.' : 'Dit bedrag wordt automatisch als negatief bedrag opgeslagen.'}</small>
                    </div>
                  </div>
                  <div className="tracker-actions-row">
                    <button
                      type="button"
                      className="card-btn tracker-primary-btn"
                      onClick={handleSavePayment}
                      disabled={
                        isSavingCost
                        || isUpdatingCost
                        || !paymentDate
                        || !paymentDescription.trim()
                        || parseNumber(paymentAmount) === null
                        || parseNumber(paymentAmount) === 0
                      }
                      aria-busy={isSavingCost || isUpdatingCost}
                    >
                      {editingPaymentId
                        ? isUpdatingCost
                          ? 'Opslaan...'
                          : 'Wijziging opslaan'
                        : isSavingCost
                          ? 'Opslaan...'
                          : 'Kostenpost opslaan'}
                    </button>
                    {editingPaymentId && (
                      <button type="button" className="card-btn card-link" onClick={handleCancelEditPayment}>
                        Annuleren
                      </button>
                    )}
                  </div>
                </article>

                <div className="tracker-cost-stats">
                  <article className="tracker-stat-card card"><h3>Abonnementskosten</h3><p>{formatCurrency(paymentStats.abonnement)}</p></article>
                  <article className="tracker-stat-card card"><h3>Overige kosten</h3><p>{formatCurrency(paymentStats.kosten)}</p></article>
                  <article className="tracker-stat-card card"><h3>Correcties</h3><p>{formatCurrency(paymentStats.correctie)}</p></article>
                  <article className="tracker-stat-card card"><h3>Netto impact</h3><p>{formatCurrency(paymentStats.netto)}</p></article>
                </div>
              </div>

              <div className="card tracker-sheet-card">
                <div className="tracker-table-wrapper">
                  <table className="tracker-table">
                    <thead>
                      <tr><th>Datum</th><th>Type</th><th>Omschrijving</th><th className="tracker-num-col">Bedrag</th><th className="tracker-bets-actions-cell">Acties</th></tr>
                    </thead>
                    <tbody>
                      {typedPayments.length === 0 ? (
                        <tr><td colSpan={5}>{trackerApiMode ? 'Nog geen kosten toegevoegd' : 'Nog geen kosten opgeslagen.'}</td></tr>
                      ) : (
                        typedPayments.map((payment) => (
                          <tr key={payment.id}>
                            <td>{payment.date}</td>
                            <td>{payment.type}</td>
                            <td className="tracker-text-ellipsis">{payment.description || '-'}</td>
                            <td className="tracker-num-col">{formatCurrency(payment.amount)}</td>
                            <td className="tracker-bets-actions-cell">
                              {payment.automatic ? (
                                <span className="tracker-auto-cost-badge">Automatisch via Stripe</span>
                              ) : (
                                <div className="tracker-table-actions">
                                  <button
                                    type="button"
                                    className="tracker-edit-btn"
                                    onClick={() => handleEditPayment(payment)}
                                    disabled={isSavingCost || isUpdatingCost || isDeletingCostId === payment.id}
                                  >
                                    Bewerken
                                  </button>
                                  <button
                                    type="button"
                                    className="tracker-danger-btn"
                                    onClick={() => handleDeletePayment(payment.id)}
                                    disabled={isDeletingCostId === payment.id}
                                    aria-busy={isDeletingCostId === payment.id}
                                  >
                                    {isDeletingCostId === payment.id ? 'Verwijderen...' : 'Verwijderen'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
              )}
            </section>
          )}
          </section>
        </section>

        {toast && (
          <div
            className={`tracker-save-toast ${toast.tone === 'error' ? 'is-error' : ''}`}
            role={toast.action ? 'button' : 'status'}
            tabIndex={0}
            aria-label={`${toast.title}. ${toast.message}`}
            onClick={toast.action ? handleToastClick : undefined}
            onKeyDown={(event) => {
              if (toast.action && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                handleToastClick();
              }
            }}
          >
            <span className="tracker-save-toast-icon" aria-hidden="true">{toast.tone === 'error' ? '!' : '✓'}</span>
            <div className="tracker-save-toast-copy">
              <strong>{toast.title}</strong>
              <span>{toast.message}</span>
            </div>
            <button
              type="button"
              className="tracker-save-toast-close"
              aria-label="Sluit melding"
              onClick={(event) => {
                event.stopPropagation();
                dismissSaveToast();
              }}
            >
              ×
            </button>
          </div>
        )}
      </>
    </PublicLayout>
  );
};

export const TrackerMainPage: React.FC = () => <TrackerWorkspacePage />;

export default TrackerWorkspacePage;
